import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Newspaper, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PressArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/press/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error loading article:', error);
      toast.error('Artikel nicht gefunden');
      navigate('/presse');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      news: { label: 'News', className: 'bg-blue-100 text-blue-700' },
      press: { label: 'Pressemitteilung', className: 'bg-green-100 text-green-700' },
      announcement: { label: 'Ankündigung', className: 'bg-purple-100 text-purple-700' },
    };
    const style = styles[category] || styles.news;
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.subtitle || article.title,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopiert!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[hsl(215,15%,45%)]">Lade Artikel...</div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Header */}
      <section className="py-12 bg-[hsl(210,40%,98%)] border-b border-[hsl(210,25%,92%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            asChild 
            className="mb-6 text-[hsl(215,15%,45%)] hover:text-[hsl(213,80%,35%)]"
          >
            <Link to="/presse">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Übersicht
            </Link>
          </Button>
          
          <div className="flex items-center gap-3 mb-4">
            {getCategoryBadge(article.category)}
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[hsl(215,25%,15%)] mb-4 leading-tight">
            {article.title}
          </h1>
          
          {article.subtitle && (
            <p className="text-xl text-[hsl(215,15%,45%)] mb-6">
              {article.subtitle}
            </p>
          )}
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6 text-sm text-[hsl(215,15%,55%)]">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(article.created_at)}
              </span>
              <span className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {article.author}
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShare}
              className="border-[hsl(210,25%,88%)]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Teilen
            </Button>
          </div>
        </div>
      </section>

      {/* Article Image */}
      {article.image_url && (
        <section className="bg-[hsl(210,40%,96%)]">
          <div className="max-w-5xl mx-auto">
            <img 
              src={article.image_url} 
              alt={article.title}
              className="w-full max-h-[500px] object-cover"
            />
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {article.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p 
                  key={index} 
                  className="text-[hsl(215,25%,30%)] leading-relaxed mb-6"
                >
                  {paragraph}
                </p>
              )
            ))}
          </article>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 bg-[hsl(210,40%,98%)] border-t border-[hsl(210,25%,92%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2">
                Weitere Artikel lesen
              </h3>
              <p className="text-[hsl(215,15%,45%)]">
                Entdecken Sie mehr Neuigkeiten und Pressemitteilungen.
              </p>
            </div>
            <Button asChild className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
              <Link to="/presse">
                <Newspaper className="w-4 h-4 mr-2" />
                Alle Artikel
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressArticle;
